// ScanProgressModal — o modal do scan de segurança.
//
// Faz o próprio polling (status/checks/progresso vêm do banco — ver get_scan_logs,
// corrige o cross-worker #25338) e compõe: cabeçalho + badge, o batimento
// (ScanCardiograph), o progresso N/M + tempo, e o checklist (ScanTimeline).
// Recarregar no meio reconstrói o estado do banco; reabrir um scan concluído vai
// direto ao veredito.

import { useEffect, useRef } from 'react'
import { Badge, Box, Group, Modal, ScrollArea, Text } from '@mantine/core'
import { IconShieldCheck } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'

import { securityApi } from '../../../../api/security'
import { useSeguirOFim } from '../../../../hooks/useSeguirOFim'
import { ScanCardiograph } from './ScanCardiograph'
import { ScanTimeline } from './ScanTimeline'
import type { ScanCheck } from './types'

const POLL_MS = 1000

interface Props {
  opened: boolean
  scanId: number | null
  tenantId: number | null
  onClose: () => void
  onComplete?: () => void
}

function isTerminal(status?: string): boolean {
  return status === 'completed' || status === 'failed'
}

function elapsedSeconds(checks: ScanCheck[], running: boolean): number | null {
  const starts = checks.map(c => c.started_at).filter(Boolean) as string[]
  if (starts.length === 0) return null
  const start = Math.min(...starts.map(s => Date.parse(s)))
  const ends = checks.map(c => c.ended_at).filter(Boolean) as string[]
  const end = running || ends.length === 0 ? Date.now() : Math.max(...ends.map(s => Date.parse(s)))
  return Math.max(0, Math.round((end - start) / 1000))
}

function mmss(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function ScanProgressModal({ opened, scanId, tenantId, onClose, onComplete }: Props) {
  const scanLogs = useQuery({
    queryKey: ['security', 'scan-logs', tenantId, scanId],
    queryFn: () => securityApi.getScanLogs(tenantId!, scanId!),
    enabled: opened && !!tenantId && !!scanId,
    refetchInterval: (query) => (isTerminal(query.state.data?.status) ? false : POLL_MS),
  })

  const data = scanLogs.data
  const status = data?.status ?? 'running'
  const checks: ScanCheck[] = data?.checks ?? []
  const running = !isTerminal(status)
  const completed = data?.progress?.completed ?? 0
  const total = data?.progress?.total ?? checks.length

  // Autoscroll do checklist enquanto roda: linha nova acompanha o fim, mas não
  // puxa quem rolou para cima (consoleai#25060).
  const timelineRef = useSeguirOFim<HTMLDivElement>(`${completed}/${total}/${status}`)

  // Dispara onComplete UMA vez quando o scan vira terminal.
  const avisadoRef = useRef(false)
  useEffect(() => {
    if (!opened) { avisadoRef.current = false; return }
    if (isTerminal(status) && !avisadoRef.current) {
      avisadoRef.current = true
      onComplete?.()
    }
  }, [opened, status, onComplete])

  const elapsed = elapsedSeconds(checks, running)
  const badgeColor = status === 'completed' ? 'green' : status === 'failed' ? 'red' : 'blue'
  const badgeLabel = status === 'completed' ? 'concluído' : status === 'failed' ? 'falhou' : 'em execução'

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      closeOnClickOutside={false}
      title={
        <Group gap="sm">
          <IconShieldCheck size={20} />
          <Text fw={600}>Scan de segurança</Text>
          <Badge color={badgeColor} variant="light">{badgeLabel}</Badge>
        </Group>
      }
    >
      <Box mb="md">
        <ScanCardiograph checks={checks} status={status} />
      </Box>

      <Group justify="space-between" mb={6}>
        <Text size="sm" c="dimmed" ff="monospace">{completed} / {total} checks</Text>
        {elapsed !== null && <Text size="sm" c="dimmed" ff="monospace">{mmss(elapsed)}</Text>}
      </Group>

      <ScrollArea h={360} viewportRef={timelineRef} type="auto">
        <ScanTimeline checks={checks} />
      </ScrollArea>
    </Modal>
  )
}
